'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

// ─── Icons ────────────────────────────────────────────────────────────────────

const PhoneIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.11 12 19.79 19.79 0 0 1 1.04 3.4 2 2 0 0 1 3 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const MenuIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="4" x2="20" y1="12" y2="12" />
    <line x1="4" x2="20" y1="6" y2="6" />
    <line x1="4" x2="20" y1="18" y2="18" />
  </svg>
);

const XIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const ArrowLeftIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m12 19-7-7 7-7" /><path d="M19 12H5" />
  </svg>
);

// ─── Logo ─────────────────────────────────────────────────────────────────────

const BilvalLogo = ({ white }: { white?: boolean }) => (
  <img
    src="/assets/images/image-1781963366139.png"
    alt="Bilvalg logo"
    className="h-9 w-auto"
    style={white ? { filter: 'brightness(0) invert(1)' } : undefined}
  />
);

// ─── Nav Links ────────────────────────────────────────────────────────────────

const homeNavLinks = [
  { label: 'Biler', href: '#biler' },
  { label: 'Innbytte', href: '#kontakt' },
  { label: 'Selg bilen din', href: '#kontakt' },
  { label: 'Kommisjon', href: '#kontakt' },
  { label: 'Kontakt', href: '#kontakt' },
];

// ─── Navbar ───────────────────────────────────────────────────────────────────

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();

  const isHome = pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // Resolve link href: on non-home pages, anchor links become /#anchor
  const resolveHref = (href: string) => {
    if (!isHome && href.startsWith('#')) return `/${href}`;
    return href;
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 border-b bg-white transition-shadow ${
        scrolled ? 'shadow-md' : 'shadow-sm'
      }`}
      style={{ borderBottomColor: 'rgba(218,223,231,0.8)' }}
    >
      <div className="container flex items-center justify-between h-16 md:h-20 px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link href="/" className="flex items-center shrink-0" aria-label="Bilvalg hjem">
          <BilvalLogo />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-5 lg:gap-8 ml-8 lg:ml-16">
          {homeNavLinks.map((link) => (
            <Link
              key={link.label}
              href={resolveHref(link.href)}
              className="text-sm font-medium transition-colors whitespace-nowrap hover:opacity-100"
              style={{ color: 'hsl(218 65% 22% / 0.75)' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'hsl(218, 65%, 22%)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'hsl(218 65% 22% / 0.75)')}
            >
              {link.label}
            </Link>
          ))}

          {/* Back to cars — only on detail pages */}
          {!isHome && (
            <Link
              href="/#biler"
              className="inline-flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap"
              style={{ color: 'hsl(218 65% 22% / 0.75)' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'hsl(218, 65%, 22%)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'hsl(218 65% 22% / 0.75)')}
            >
              <ArrowLeftIcon />
              Alle biler
            </Link>
          )}

          <a
            href="tel:+4741159508"
            className="inline-flex items-center gap-2 px-4 lg:px-5 py-2.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90 active:scale-[0.97] whitespace-nowrap ml-2"
            style={{ backgroundColor: 'hsl(218, 65%, 22%)', color: '#fff' }}
          >
            <PhoneIcon className="w-4 h-4" />
            Ring oss: 411 59 508
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 active:scale-95 transition-transform"
          style={{ color: 'hsl(218, 65%, 22%)' }}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? 'Lukk meny' : 'Åpne meny'}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <XIcon className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t" style={{ borderColor: 'rgba(218,223,231,0.8)' }}>
          <div className="px-4 sm:px-6 py-4 flex flex-col gap-1">
            {homeNavLinks.map((link) => (
              <Link
                key={link.label}
                href={resolveHref(link.href)}
                className="text-sm font-medium py-3 px-3 rounded-lg transition-colors block"
                style={{ color: 'hsl(218, 65%, 22%)' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'hsl(218, 65%, 22%, 0.06)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </Link>
            ))}

            {/* Back to cars — only on detail pages */}
            {!isHome && (
              <Link
                href="/#biler"
                className="inline-flex items-center gap-2 text-sm font-medium py-3 px-3 rounded-lg transition-colors"
                style={{ color: 'hsl(218, 65%, 22%)' }}
                onClick={() => setMobileOpen(false)}
              >
                <ArrowLeftIcon />
                Alle biler
              </Link>
            )}

            <div className="pt-3 border-t mt-2" style={{ borderColor: 'rgba(218,223,231,0.8)' }}>
              <a
                href="tel:+4741159508"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold w-full justify-center"
                style={{ backgroundColor: 'hsl(218, 65%, 22%)', color: '#fff' }}
              >
                <PhoneIcon className="w-4 h-4" />
                Ring oss: 411 59 508
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
